import {
  alibabaTradeGetBuyerView2,
  queryGlobalTbGoodsOrderList2,
  queryJyOrderAlibaba,
} from '../user-center/src/api/mall.ts'
import {
  buildTbPixelPurchaseDetailQuery,
  resolveAlibabaPixelPurchaseDecision,
  resolveTbPixelPurchaseDecision,
} from './pendingPixelPurchaseRules.js'
import { hasActivePixelAnalytics, trackValidatedPixelPurchase } from './pixelAnalytics.js'

const STORAGE_KEY = 'mall_pending_pixel_purchases_v1'
const FIRST_CHECK_DELAY_MS = 3 * 1000
const HEARTBEAT_INTERVAL_MS = 60 * 1000
const MAX_RECORD_AGE_MS = 30 * 24 * 60 * 60 * 1000
const MAX_RECORDS_PER_FLUSH = 20
const MAX_CONTENT_COUNT = 50

let heartbeatEnabled = false
let heartbeatTimer = null
let firstCheckTimer = null
let requestedCheckTimer = null
let isFlushing = false
let listenersAttached = false

function getWindow() {
  return typeof window !== 'undefined' ? window : null
}

function getPath(source, path) {
  return String(path || '')
    .split('.')
    .filter(Boolean)
    .reduce((value, key) => value?.[key], source)
}

function toText(value) {
  return String(value ?? '').trim()
}

function toNumber(value) {
  if (value === undefined || value === null || value === '')
    return undefined
  const numberValue = Number(value)
  return Number.isFinite(numberValue) ? numberValue : undefined
}

function toMajorAmount(value, amountUnit = 'minor') {
  const numberValue = toNumber(value)
  if (numberValue === undefined)
    return undefined
  return amountUnit === 'major' ? numberValue : numberValue / 100
}

function compactIds(...values) {
  const result = []
  values.flat(Infinity).forEach((value) => {
    String(value ?? '')
      .split(',')
      .map(item => item.trim())
      .filter(Boolean)
      .forEach(item => result.push(item))
  })
  return [...new Set(result)]
}

function normalizeContents(items, amountUnit) {
  if (!Array.isArray(items))
    return []
  return items
    .map((item) => {
      if (!item || typeof item !== 'object')
        return null
      const itemId = toText(item.itemId || item.item_id || item.offerId || item.content_id)
      const itemSkuId = toText(item.itemSkuId || item.skuId || item.sku_id || item.specId)
      if (!itemId && !itemSkuId)
        return null
      return {
        itemId: itemId || itemSkuId,
        itemSkuId: itemSkuId || undefined,
        title: toText(item.title || item.itemName || item.content_name) || undefined,
        price: toMajorAmount(item.pixelPrice ?? item.price, item.amountUnit || amountUnit),
        quantity: toNumber(item.quantity) || 1,
        amountUnit: 'major',
      }
    })
    .filter(Boolean)
    .slice(0, MAX_CONTENT_COUNT)
}

function parseStoredQueue() {
  const win = getWindow()
  if (!win?.localStorage)
    return []
  try {
    const parsed = JSON.parse(win.localStorage.getItem(STORAGE_KEY) || '[]')
    return Array.isArray(parsed) ? parsed : []
  }
  catch {
    return []
  }
}

function normalizeRecord(record) {
  if (!record || !['tbGlobal', 'alibaba1688'].includes(record.platform))
    return null
  const orderId = toText(record.orderId)
  if (!orderId)
    return null
  const now = Date.now()
  return {
    ...record,
    orderId,
    orderCode: toText(record.orderCode) || undefined,
    outerOrderId: toText(record.outerOrderId) || undefined,
    purchaseIds: compactIds(record.purchaseIds),
    orderIds: compactIds(record.orderIds),
    amount: toNumber(record.amount),
    currency: toText(record.currency || 'CNY').toUpperCase() || 'CNY',
    contents: normalizeContents(record.contents, 'major'),
    createdAt: toNumber(record.createdAt) || now,
    updatedAt: toNumber(record.updatedAt) || now,
    attemptCount: toNumber(record.attemptCount) || 0,
  }
}

function getRecordKey(record) {
  return `${record.platform}:${record.orderId}`
}

function readQueue() {
  const now = Date.now()
  return parseStoredQueue()
    .map(normalizeRecord)
    .filter(record => record && now - record.createdAt <= MAX_RECORD_AGE_MS)
}

function writeQueue(queue) {
  const win = getWindow()
  if (!win?.localStorage)
    return false
  try {
    win.localStorage.setItem(STORAGE_KEY, JSON.stringify(queue))
    return true
  }
  catch {
    return false
  }
}

export function getPendingPixelPurchaseQueue() {
  return readQueue()
}

function buildPendingRecords(platform, params = {}) {
  const amountUnit = params.amountUnit || 'minor'
  const purchaseIds = compactIds(params.purchaseIds, params.purchaseId, params.purchase_id)
  const orderIds = compactIds(params.orderIds, params.orderId, params.order_id)
  const orderCode = toText(params.orderCode || params.order_code)
  const outerOrderId = toText(params.outerOrderId || params.outer_order_id)
  const recordIds = platform === 'tbGlobal'
    ? (purchaseIds.length ? purchaseIds : orderIds.length ? orderIds : compactIds(orderCode))
    : (orderIds.length ? orderIds : compactIds(orderCode))
  if (!recordIds.length)
    return []

  const totalAmount = toMajorAmount(
    params.pixelValue
    ?? params.totalPrice
    ?? params.total_price
    ?? params.totalAmount
    ?? params.total_amount
    ?? params.value,
    amountUnit,
  )
  const contents = normalizeContents(params.pixelContents || params.items, amountUnit)
  const totalQuantity = toNumber(params.totalQuantity)
    || contents.reduce((total, item) => total + (item.quantity || 1), 0)
  const now = Date.now()

  return recordIds.map(orderId => ({
    platform,
    orderId,
    orderCode: orderCode || undefined,
    outerOrderId: platform === 'tbGlobal'
      ? (outerOrderId || (purchaseIds.length ? toText(params.orderId) : '') || undefined)
      : undefined,
    purchaseIds,
    orderIds,
    amount: recordIds.length === 1 ? totalAmount : undefined,
    currency: toText(params.currency || params.currencyCode || 'CNY').toUpperCase() || 'CNY',
    contents: recordIds.length === 1 ? contents : [],
    itemCount: toNumber(params.itemCount) || contents.length || undefined,
    totalQuantity: totalQuantity || undefined,
    payType: toText(params.payType || params.pay_type) || undefined,
    payMethod: toText(params.payMethod || params.pay_method) || undefined,
    source: toText(params.source) || 'order_create_success',
    createdAt: now,
    updatedAt: now,
    attemptCount: 0,
  }))
}

export function enqueuePendingPixelPurchase(platform, params = {}) {
  if (!getWindow() || !['tbGlobal', 'alibaba1688'].includes(platform))
    return 0
  const records = buildPendingRecords(platform, params)
  if (!records.length)
    return 0

  const recordMap = new Map(readQueue().map(record => [getRecordKey(record), record]))
  records.forEach((record) => {
    const key = getRecordKey(record)
    const previous = recordMap.get(key)
    recordMap.set(key, normalizeRecord({
      ...(previous || {}),
      ...record,
      amount: record.amount || previous?.amount,
      contents: record.contents?.length ? record.contents : previous?.contents,
      createdAt: previous?.createdAt || record.createdAt,
      attemptCount: previous?.attemptCount || 0,
      lastError: undefined,
    }))
  })
  writeQueue([...recordMap.values()].filter(Boolean))
  requestPendingPixelPurchaseCheck(FIRST_CHECK_DELAY_MS)
  return records.length
}

async function fetchTbDetail(record) {
  const query = buildTbPixelPurchaseDetailQuery(record)
  const queryDetailList = async (queryParams) => {
    const response = await queryGlobalTbGoodsOrderList2({
      pageNo: 0,
      pageSize: 10,
      ...queryParams,
      __time: Date.now(),
      param: { hideToastTip: 1 },
    })
    const responseList = getPath(response, 'data.convertData.dataList')
      || getPath(response, 'data.dataList')
      || []
    return Array.isArray(responseList) ? responseList : []
  }

  let list = await queryDetailList(query)
  if (!list.length && query.orderId && query.purchaseId) {
    list = await queryDetailList({
      orderId: query.orderId,
      orderCode: query.orderCode,
    })
  }
  if (!Array.isArray(list))
    return null

  const expectedOrderIds = compactIds(record.outerOrderId, record.orderIds)
  const expectedPurchaseIds = compactIds(record.orderId, record.purchaseIds)
  return list.find(item => (
    expectedPurchaseIds.includes(toText(item?.purchaseId))
    || expectedOrderIds.includes(toText(item?.orderId))
    || toText(item?.orderId) === record.orderId
    || (record.orderCode && toText(item?.orderCode) === record.orderCode)
  )) || (list.length === 1 ? list[0] : null)
}

function getAlibabaResponseDetail(response) {
  const responseData = getPath(response, 'data') || {}
  const detail = responseData.convertData || responseData.originalData || responseData
  if (!detail || typeof detail !== 'object')
    return null
  return {
    ...detail,
    commissionFee: detail.commissionFee ?? responseData.commissionFee,
    commissionRate: detail.commissionRate ?? responseData.commissionRate,
  }
}

async function fetchAlibabaDetail(record) {
  const queryDetail = async (orderId) => {
    if (!orderId)
      return null
    const response = await alibabaTradeGetBuyerView2({
      param: {
        orderId,
        webSite: '1688',
        includeFields: null,
        hideToastTip: 1,
      },
    })
    return getAlibabaResponseDetail(response)
  }

  let detail = await queryDetail(record.orderId)
  if (detail?.baseInfo || detail?.productItems)
    return detail

  const listResponse = await queryJyOrderAlibaba({
    param: {
      pageNo: 0,
      pageSize: 20,
      orderId: record.orderId,
      orderCode: record.orderCode || record.orderId,
      hideToastTip: 1,
    },
  })
  const list = getPath(listResponse, 'data.convertData.dataList')
    || getPath(listResponse, 'data.dataList')
    || []
  let listItem = null
  if (Array.isArray(list)) {
    listItem = list.find(item => (
      toText(item?.orderId) === record.orderId
      || (record.orderCode && toText(item?.orderCode) === record.orderCode)
      || toText(item?.orderCode) === record.orderId
    )) || (list.length === 1 ? list[0] : null)
  }
  const resolvedOrderId = toText(listItem?.orderId)
  if (resolvedOrderId && resolvedOrderId !== record.orderId) {
    detail = await queryDetail(resolvedOrderId)
    if (detail) {
      detail.orderId = detail.orderId || resolvedOrderId
      detail.orderCode = detail.orderCode || record.orderCode
      return detail
    }
  }
  return detail || listItem || null
}

function buildDetailContents(platform, detail) {
  const sourceItems = platform === 'tbGlobal'
    ? getPath(detail, 'detailResults')
    : getPath(detail, 'productItems') || getPath(detail, 'detailResults')
  if (!Array.isArray(sourceItems))
    return []

  return sourceItems
    .map((item) => {
      const quantity = toNumber(item?.quantity || item?.amount) || 1
      const rawPrice = platform === 'tbGlobal'
        ? toNumber(item?.price || item?.itemPrice)
        : toNumber(item?.price || item?.unitPrice || item?.productPrice)
      return {
        itemId: item?.itemId || item?.offerId || item?.productID || item?.productId,
        itemSkuId: item?.itemSkuId || item?.skuId || item?.specId || item?.skuID,
        title: item?.title || item?.name || item?.productName,
        price: rawPrice === undefined
          ? undefined
          : platform === 'tbGlobal' ? rawPrice / 100 : rawPrice,
        quantity,
        amountUnit: 'major',
      }
    })
    .filter(item => item.itemId || item.itemSkuId)
    .slice(0, MAX_CONTENT_COUNT)
}

function buildValidatedPurchasePayload(record, decision) {
  const detailContents = buildDetailContents(record.platform, decision.detail || {})
  const contents = detailContents.length ? detailContents : record.contents
  const resolvedPurchaseIds = record.platform === 'tbGlobal'
    ? compactIds(decision.detail?.purchaseId, record.purchaseIds)
    : record.purchaseIds
  const resolvedOrderIds = record.platform === 'tbGlobal'
    ? compactIds(decision.detail?.orderId, record.orderIds)
    : record.orderIds
  return {
    attributionValidated: true,
    source: record.source || 'pending_purchase_heartbeat',
    platform: record.platform,
    orderId: record.orderId,
    orderCode: record.orderCode,
    purchaseIds: resolvedPurchaseIds,
    orderIds: resolvedOrderIds,
    payType: record.payType,
    payMethod: record.payMethod,
    totalPrice: decision.amount,
    pixelValue: decision.amount,
    amountUnit: 'major',
    currency: record.currency || 'CNY',
    itemCount: contents.length || record.itemCount || 1,
    totalQuantity: record.totalQuantity,
    items: contents,
    pendingCreatedAt: record.createdAt,
    pendingAttemptCount: record.attemptCount || 0,
  }
}

function updatePendingRecord(record, error) {
  return {
    ...record,
    updatedAt: Date.now(),
    lastCheckedAt: Date.now(),
    attemptCount: (record.attemptCount || 0) + 1,
    lastError: error,
  }
}

async function resolveRecordDecision(record) {
  if (record.platform === 'tbGlobal') {
    const detail = await fetchTbDetail(record)
    return resolveTbPixelPurchaseDecision(detail, record.amount)
  }
  const detail = await fetchAlibabaDetail(record)
  return resolveAlibabaPixelPurchaseDecision(detail, record.amount)
}

export async function flushPendingPixelPurchases() {
  const win = getWindow()
  if (!win || isFlushing || !heartbeatEnabled || !hasActivePixelAnalytics())
    return false
  if (win.navigator?.onLine === false)
    return false

  isFlushing = true
  try {
    const queue = readQueue()
    if (!queue.length)
      return true

    const removeKeys = new Set()
    const updates = new Map()
    for (const record of queue.slice(0, MAX_RECORDS_PER_FLUSH)) {
      const key = getRecordKey(record)
      try {
        const decision = await resolveRecordDecision(record)
        if (decision.type === 'discard') {
          removeKeys.add(key)
          continue
        }
        if (decision.type === 'report') {
          const payload = buildValidatedPurchasePayload(record, decision)
          if (!decision.amount || decision.amount <= 0) {
            updates.set(key, updatePendingRecord(record, 'missing_positive_amount'))
            continue
          }
          const tracked = trackValidatedPixelPurchase(
            record.platform === 'tbGlobal' ? 'tb_payment_success' : 'alibaba_payment_success',
            payload,
          )
          if (tracked) {
            removeKeys.add(key)
            continue
          }
          updates.set(key, updatePendingRecord(record, 'pixel_not_ready_or_track_failed'))
          continue
        }
        updates.set(key, updatePendingRecord(record, decision.reason))
      }
      catch (error) {
        updates.set(key, updatePendingRecord(record, error?.message || String(error)))
      }
    }

    const nextQueue = readQueue()
      .filter(record => !removeKeys.has(getRecordKey(record)))
      .map(record => updates.get(getRecordKey(record)) || record)
    writeQueue(nextQueue)
    return true
  }
  finally {
    isFlushing = false
  }
}

function runHeartbeatCheck() {
  void flushPendingPixelPurchases()
}

function onVisibilityChange() {
  const win = getWindow()
  if (!win?.document || win.document.visibilityState === 'visible')
    runHeartbeatCheck()
}

function attachHeartbeatListeners() {
  const win = getWindow()
  if (!win || listenersAttached)
    return
  win.addEventListener('focus', runHeartbeatCheck)
  win.addEventListener('online', runHeartbeatCheck)
  win.document?.addEventListener('visibilitychange', onVisibilityChange)
  listenersAttached = true
}

function detachHeartbeatListeners() {
  const win = getWindow()
  if (!win || !listenersAttached)
    return
  win.removeEventListener('focus', runHeartbeatCheck)
  win.removeEventListener('online', runHeartbeatCheck)
  win.document?.removeEventListener('visibilitychange', onVisibilityChange)
  listenersAttached = false
}

function clearHeartbeatTimers() {
  const win = getWindow()
  if (!win)
    return
  if (firstCheckTimer)
    win.clearTimeout(firstCheckTimer)
  if (heartbeatTimer)
    win.clearInterval(heartbeatTimer)
  if (requestedCheckTimer)
    win.clearTimeout(requestedCheckTimer)
  firstCheckTimer = null
  heartbeatTimer = null
  requestedCheckTimer = null
}

export function requestPendingPixelPurchaseCheck(delayMs = 0) {
  const win = getWindow()
  if (!win || !heartbeatEnabled)
    return false
  if (requestedCheckTimer)
    win.clearTimeout(requestedCheckTimer)
  requestedCheckTimer = win.setTimeout(() => {
    requestedCheckTimer = null
    runHeartbeatCheck()
  }, Math.max(0, Number(delayMs) || 0))
  return true
}

export function setPendingPixelPurchaseHeartbeatEnabled(enabled) {
  const win = getWindow()
  if (!win)
    return false
  const nextEnabled = Boolean(enabled)
  if (heartbeatEnabled === nextEnabled)
    return heartbeatEnabled

  heartbeatEnabled = nextEnabled
  clearHeartbeatTimers()
  detachHeartbeatListeners()
  if (!heartbeatEnabled)
    return false

  attachHeartbeatListeners()
  firstCheckTimer = win.setTimeout(runHeartbeatCheck, FIRST_CHECK_DELAY_MS)
  heartbeatTimer = win.setInterval(runHeartbeatCheck, HEARTBEAT_INTERVAL_MS)
  return true
}
